import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import * as XLSX from 'xlsx';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
} from 'chart.js';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const ALL_BREAKDOWN_COLUMNS = [
    { key: 'identification', label: 'CMP / ID', pdfWidthWeight: 30 },
    { key: 'name', label: 'Nombre Completo', pdfWidthWeight: 50 },
    { key: 'email', label: 'Correo Electrónico', pdfWidthWeight: 55 },
    { key: 'city', label: 'Ciudad', pdfWidthWeight: 32 },
    { key: 'sala_name', label: 'Sala', pdfWidthWeight: 35 },
    { key: 'ingreso', label: 'Ingreso / Confirmación', pdfWidthWeight: 28 },
    { key: 'firmo', label: 'Firma Registrada', pdfWidthWeight: 28 },
    { key: 'voto', label: 'Voto Registrado', pdfWidthWeight: 28 }
];

const Reports = () => {
    // 'stats' | 'pre_attendance' | 'event_attendance' | 'all_users_breakdown' | 'polls_trivia'
    const [activeTab, setActiveTab] = useState('stats'); 
    const [salas, setSalas] = useState([]);
    const [selectedSala, setSelectedSala] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [stats, setStats] = useState(null);
    const reportRef = useRef(null);

    // Lists & Selection States
    const [allUsers, setAllUsers] = useState([]);
    const [preSelected, setPreSelected] = useState([]);
    const [eventSelected, setEventSelected] = useState([]);
    const [breakdownSelected, setBreakdownSelected] = useState([]);
    const [pollsList, setPollsList] = useState([]);

    // Exclusion States for Statistics
    const [excludedUserIds, setExcludedUserIds] = useState([]);
    const [isExcludeModalOpen, setIsExcludeModalOpen] = useState(false);

    // Search & Filter States
    const [preSearch, setPreSearch] = useState('');
    const [preCityFilter, setPreCityFilter] = useState('');
    const [eventSearch, setEventSearch] = useState('');
    const [eventCityFilter, setEventCityFilter] = useState('');
    const [breakdownSearch, setBreakdownSearch] = useState('');
    const [breakdownSalaFilter, setBreakdownSalaFilter] = useState('');

    useEffect(() => {
        fetchSalas();
        fetchUsers();
        fetchPolls();
    }, []);

    useEffect(() => {
        fetchStats();
    }, [selectedSala, selectedDate, excludedUserIds]);

    const fetchSalas = async () => {
        try {
            const res = await api.get('/salas');
            if (res.data?.success) {
                setSalas(res.data.data);
            } else if (Array.isArray(res.data)) {
                setSalas(res.data);
            }
        } catch (error) {
            console.error("Error fetching salas:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await api.get('/users');
            const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setAllUsers(data);
            
            // Default selections
            const preUsers = data.filter(u => u.asistire === 1 || u.asistire_date || (u.city && u.city.trim() !== ''));
            setPreSelected(preUsers.map(u => u.id));

            const evtUsers = data.filter(u => u.asistencia_evento === 1 || u.asistencia_evento_date);
            setEventSelected(evtUsers.map(u => u.id));

            setBreakdownSelected(data.map(u => u.id));
        } catch (error) {
            console.error("Error fetching users list:", error);
        }
    };

    const fetchPolls = async () => {
        try {
            let res;
            try {
                res = await api.get('/polls/all');
            } catch (e) {
                res = await api.get('/polls');
            }
            const pollsData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            
            // Fetch stats for each poll
            const pollsWithStats = await Promise.all(pollsData.map(async (p) => {
                try {
                    const sRes = await api.get(`/polls/${p.id}/stats`);
                    return { ...p, stats: sRes.data };
                } catch (e) {
                    return { ...p, stats: null };
                }
            }));
            setPollsList(pollsWithStats);
        } catch (error) {
            console.error("Error fetching polls:", error);
        }
    };

    const fetchStats = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedSala) params.append('sala_id', selectedSala);
            if (selectedDate) params.append('date', selectedDate);
            if (excludedUserIds.length > 0) params.append('exclude_ids', excludedUserIds.join(','));

            const res = await api.get(`/reports/comprehensive?${params.toString()}`);
            if (res.data?.success) {
                setStats(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const handleExportPDF = async () => {
        if (!reportRef.current) return;

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pages = reportRef.current.querySelectorAll('.pdf-page');

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    logging: false
                });

                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            }

            pdf.save(`Reporte_Estadisticas_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Hubo un error al generar el PDF.");
        }
    };

    // Filtered lists
    const preAttendanceUsers = allUsers.filter(u => {
        const hasPre = u.asistire === 1 || u.asistire_date || (u.city && u.city.trim() !== '');
        if (!hasPre) return false;
        if (preCityFilter && (u.city || '').toLowerCase() !== preCityFilter.toLowerCase()) return false;
        if (preSearch) {
            const q = preSearch.toLowerCase();
            const fullStr = `${u.identification || ''} ${u.name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
            if (!fullStr.includes(q)) return false;
        }
        return true;
    });

    const eventAttendanceUsers = allUsers.filter(u => {
        const hasEvt = u.asistencia_evento === 1 || u.asistencia_evento_date;
        if (!hasEvt) return false;
        if (eventCityFilter && (u.city || '').toLowerCase() !== eventCityFilter.toLowerCase()) return false;
        if (eventSearch) {
            const q = eventSearch.toLowerCase();
            const fullStr = `${u.identification || ''} ${u.name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
            if (!fullStr.includes(q)) return false;
        }
        return true;
    });

    const rawBreakdownList = (stats?.user_details && stats.user_details.length > 0)
        ? stats.user_details
        : allUsers.map(u => ({
            id: u.id,
            identification: u.identification,
            name: u.name,
            last_name: u.last_name,
            email: u.email,
            city: u.city,
            sala_name: u.sala_name || 'Sin Sala',
            ingreso: (u.asistencia_evento === 1 || u.asistire === 1) ? 'Sí' : 'No',
            firmo: u.signature ? 'Sí' : 'No',
            voto: u.voto || 'No'
        }));

    // Breakdown Column Selection State
    const [selectedBreakdownColumns, setSelectedBreakdownColumns] = useState([
        'identification', 'name', 'email', 'city', 'sala_name', 'ingreso', 'firmo', 'voto'
    ]);

    const toggleBreakdownColumn = (key) => {
        setSelectedBreakdownColumns(prev => {
            if (prev.includes(key)) {
                if (prev.length === 1) return prev;
                return prev.filter(k => k !== key);
            }
            return [...prev, key];
        });
    };

    const selectAllBreakdownColumns = () => {
        setSelectedBreakdownColumns(ALL_BREAKDOWN_COLUMNS.map(c => c.key));
    };

    const exportBreakdownToExcel = () => {
        const selectedUserObjects = userBreakdownList.filter(u => breakdownSelected.includes(u.id || `${u.name}-${u.last_name}`));
        if (!selectedUserObjects || selectedUserObjects.length === 0) {
            alert("Por favor selecciona al menos un usuario para exportar a Excel.");
            return;
        }

        const activeCols = ALL_BREAKDOWN_COLUMNS.filter(c => selectedBreakdownColumns.includes(c.key));

        const exportData = selectedUserObjects.map((u, idx) => {
            const row = { '#': idx + 1 };
            activeCols.forEach(col => {
                if (col.key === 'identification') row[col.label] = u.identification || '—';
                else if (col.key === 'name') row[col.label] = `${u.name || ''} ${u.last_name || ''}`.trim() || '—';
                else if (col.key === 'email') row[col.label] = u.email || '—';
                else if (col.key === 'city') row[col.label] = u.city || '—';
                else if (col.key === 'sala_name') row[col.label] = u.sala_name || 'Sin Sala';
                else if (col.key === 'ingreso') row[col.label] = u.ingreso || 'No';
                else if (col.key === 'firmo') row[col.label] = u.firmo || 'No';
                else if (col.key === 'voto') row[col.label] = u.voto || 'No';
            });
            return row;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "Detalle Usuarios");
        XLSX.writeFile(wb, `Reporte_Detalle_Usuarios_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const userBreakdownList = rawBreakdownList.filter(u => {
        if (breakdownSalaFilter && (u.sala_name || '').toLowerCase() !== breakdownSalaFilter.toLowerCase()) return false;
        if (breakdownSearch) {
            const q = breakdownSearch.toLowerCase();
            const fullStr = `${u.identification || ''} ${u.name || ''} ${u.last_name || ''} ${u.email || ''} ${u.city || ''}`.toLowerCase();
            if (!fullStr.includes(q)) return false;
        }
        return true;
    });

    // Checkbox Handlers
    const togglePreUser = (id) => setPreSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const selectAllPre = () => setPreSelected(preAttendanceUsers.map(u => u.id));
    const deselectAllPre = () => setPreSelected([]);

    const toggleEventUser = (id) => setEventSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const selectAllEvent = () => setEventSelected(eventAttendanceUsers.map(u => u.id));
    const deselectAllEvent = () => setEventSelected([]);

    const toggleBreakdownUser = (id) => setBreakdownSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const selectAllBreakdown = () => setBreakdownSelected(userBreakdownList.map(u => u.id || `${u.name}-${u.last_name}`));
    const deselectAllBreakdown = () => setBreakdownSelected([]);

    // PURE VECTOR TEXT PDF EXPORT (No html2canvas image) FOR USER LISTS
    const exportSelectedToVectorPDF = (reportTitle, selectedUserObjects, typeName) => {
        if (!selectedUserObjects || selectedUserObjects.length === 0) {
            alert("Por favor selecciona al menos un usuario para exportar a PDF.");
            return;
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;

        // Banner Header
        doc.setFillColor(0, 89, 91); // Theme primary #00595b
        doc.rect(0, 0, pageWidth, 24, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(`REPORTE DE USUARIOS - ${reportTitle.toUpperCase()}`, margin, 11);

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        const dateStr = new Date().toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' });
        doc.text(`Emisión: ${dateStr} | Seleccionados: ${selectedUserObjects.length}`, pageWidth - margin, 11, { align: 'right' });
        doc.text(`UN HITO en la Prevención frente al VRS — Pfizer`, margin, 18);

        let startY = 30;
        const isBreakdown = typeName === 'breakdown';
        let colWidths = [];
        let headers = [];

        if (isBreakdown) {
            const activeCols = ALL_BREAKDOWN_COLUMNS.filter(c => selectedBreakdownColumns.includes(c.key));
            headers = ["#", ...activeCols.map(c => c.label)];
            
            const totalWeight = 10 + activeCols.reduce((sum, c) => sum + c.pdfWidthWeight, 0);
            const availableWidth = pageWidth - (margin * 2);
            colWidths = [
                Math.round((10 / totalWeight) * availableWidth),
                ...activeCols.map(c => Math.round((c.pdfWidthWeight / totalWeight) * availableWidth))
            ];
        } else {
            colWidths = [12, 38, 65, 75, 35, 45];
            headers = ["#", "CMP / ID", "Nombre Completo", "Correo Electrónico", "Ciudad", "Fecha / Hora"];
        }

        const drawTableHeader = (y) => {
            doc.setFillColor(235, 240, 245);
            doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);

            let currX = margin + 2;
            headers.forEach((h, i) => {
                doc.text(h, currX, y + 5.5);
                currX += colWidths[i];
            });
        };

        drawTableHeader(startY);
        startY += 9;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        selectedUserObjects.forEach((row, idx) => {
            if (startY > pageHeight - 16) {
                doc.addPage();
                doc.setFillColor(0, 89, 91);
                doc.rect(0, 0, pageWidth, 16, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.text(`REPORTE DE USUARIOS - ${reportTitle.toUpperCase()} (Cont.)`, margin, 11);

                startY = 22;
                drawTableHeader(startY);
                startY += 9;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
            }

            if (idx % 2 === 0) {
                doc.setFillColor(248, 250, 252);
                doc.rect(margin, startY - 3.5, pageWidth - (margin * 2), 6.5, 'F');
            }

            doc.setTextColor(51, 65, 85);
            let currX = margin + 2;

            let rowValues = [];
            if (isBreakdown) {
                const activeCols = ALL_BREAKDOWN_COLUMNS.filter(c => selectedBreakdownColumns.includes(c.key));
                rowValues = [
                    String(idx + 1),
                    ...activeCols.map(c => {
                        if (c.key === 'identification') return row.identification || '—';
                        if (c.key === 'name') return `${row.name || ''} ${row.last_name || ''}`.trim() || '—';
                        if (c.key === 'email') return row.email || '—';
                        if (c.key === 'city') return row.city || '—';
                        if (c.key === 'sala_name') return row.sala_name || 'Sin Sala';
                        if (c.key === 'ingreso') return row.ingreso || 'No';
                        if (c.key === 'firmo') return row.firmo || 'No';
                        if (c.key === 'voto') return row.voto || 'No';
                        return '—';
                    })
                ];
            } else {
                const rawDate = typeName === 'pre' ? (row.asistire_date || row.created_at) : (row.asistencia_evento_date || row.created_at);
                const dateFormatted = rawDate 
                    ? new Date(rawDate).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) 
                    : '—';

                rowValues = [
                    String(idx + 1),
                    row.identification || '—',
                    `${row.name || ''} ${row.last_name || ''}`.trim() || '—',
                    row.email || '—',
                    row.city || '—',
                    dateFormatted
                ];
            }

            rowValues.forEach((val, i) => {
                const currColWidth = colWidths[i] || 25;
                const approxMaxChars = Math.max(8, Math.floor(currColWidth * 0.75));
                const textStr = String(val).length > approxMaxChars ? String(val).substring(0, approxMaxChars) + '…' : String(val);
                doc.text(textStr, currX, startY + 1);
                currX += colWidths[i];
            });

            doc.setDrawColor(226, 232, 240);
            doc.line(margin, startY + 3, pageWidth - margin, startY + 3);
            startY += 6.5;
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
            doc.setPage(p);
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text(`Página ${p} de ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
            doc.text(`Documento generado en formato texto vectorial seleccionable (PDF)`, margin, pageHeight - 6);
        }

        const filename = `Reporte_${typeName}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
    };

    // PURE VECTOR TEXT PDF EXPORT FOR INTERACTIVE POLLS & TRIVIAS
    const exportPollsToVectorPDF = () => {
        if (!pollsList || pollsList.length === 0) {
            alert("No hay trivias o encuestas para exportar.");
            return;
        }

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 14;

        // Header Banner
        doc.setFillColor(0, 89, 91); // #00595b
        doc.rect(0, 0, pageWidth, 26, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text("RESULTADOS DE PREGUNTAS INTERACTIVAS Y TRIVIAS", margin, 12);

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        const dateStr = new Date().toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' });
        doc.text(`Fecha emisión: ${dateStr}`, pageWidth - margin, 12, { align: 'right' });
        doc.text(`UN HITO en la Prevención frente al VRS — Pfizer`, margin, 19);

        let startY = 34;

        pollsList.forEach((poll, pIdx) => {
            if (startY > pageHeight - 45) {
                doc.addPage();
                doc.setFillColor(0, 89, 91);
                doc.rect(0, 0, pageWidth, 16, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.text("RESULTADOS DE PREGUNTAS INTERACTIVAS (Cont.)", margin, 11);
                startY = 24;
            }

            // Question Header
            doc.setFillColor(241, 245, 249);
            doc.rect(margin, startY, pageWidth - (margin * 2), 10, 'F');
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.text(`Pregunta ${pIdx + 1}: ${poll.question || 'Sin Título'}`, margin + 3, startY + 6.5);

            startY += 12;

            // Options Table Header
            doc.setFillColor(226, 232, 240);
            doc.rect(margin, startY, pageWidth - (margin * 2), 7, 'F');
            doc.setFontSize(8.5);
            doc.setTextColor(51, 65, 85);
            doc.text("Opción / Respuesta", margin + 3, startY + 5);
            doc.text("Votos", pageWidth - margin - 45, startY + 5, { align: 'right' });
            doc.text("Porcentaje", pageWidth - margin - 6, startY + 5, { align: 'right' });

            startY += 8;

            const results = poll.stats?.results || poll.options || [];
            const totalVotes = poll.stats?.totalVotes || results.reduce((sum, r) => sum + (r.votes || 0), 0);

            results.forEach((res, rIdx) => {
                if (startY > pageHeight - 20) {
                    doc.addPage();
                    startY = 20;
                }

                const votes = res.votes || 0;
                const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;

                if (rIdx % 2 === 0) {
                    doc.setFillColor(248, 250, 252);
                    doc.rect(margin, startY - 3, pageWidth - (margin * 2), 6, 'F');
                }

                doc.setFont('helvetica', res.is_correct ? 'bold' : 'normal');
                doc.setTextColor(res.is_correct ? 16 : 51, res.is_correct ? 185 : 65, res.is_correct ? 129 : 85);

                const optText = `${res.is_correct ? '✓ ' : ''}${res.option_text || res.text || ''}`;
                doc.text(optText, margin + 3, startY + 1.5);

                doc.setTextColor(51, 65, 85);
                doc.text(`${votes} votos`, pageWidth - margin - 45, startY + 1.5, { align: 'right' });
                doc.text(`${percent}%`, pageWidth - margin - 6, startY + 1.5, { align: 'right' });

                doc.setDrawColor(241, 245, 249);
                doc.line(margin, startY + 3, pageWidth - margin, startY + 3);

                startY += 6.5;
            });

            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text(`Total votos registrados en esta pregunta: ${totalVotes}`, margin + 3, startY + 2);

            startY += 10;
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
            doc.setPage(p);
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text(`Página ${p} de ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
            doc.text(`Estadísticas descargables en formato texto vectorial (PDF)`, margin, pageHeight - 6);
        }

        doc.save(`Resultados_Trivias_Interactivas_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    // Stats calculations
    const totalSignature = stats ? (stats.with_signature + stats.without_signature) : 0;
    const withSignaturePct = totalSignature ? ((stats.with_signature / totalSignature) * 100).toFixed(1) : 0;
    const withoutSignaturePct = totalSignature ? ((stats.without_signature / totalSignature) * 100).toFixed(1) : 0;

    const signatureData = stats ? {
        labels: [`Con Firma: ${stats.with_signature} (${withSignaturePct}%)`, `Sin Firma: ${stats.without_signature} (${withoutSignaturePct}%)`],
        datasets: [{
            data: [stats.with_signature, stats.without_signature],
            backgroundColor: ['#10b981', '#f43f5e'],
            borderWidth: 1,
        }]
    } : null;

    const totalParticipation = stats ? (stats.voted + stats.entered_not_voted + stats.not_entered_not_voted) : 0;
    const votedPct = totalParticipation ? ((stats.voted / totalParticipation) * 100).toFixed(1) : 0;
    const enteredNotVotedPct = totalParticipation ? ((stats.entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;
    const notEnteredNotVotedPct = totalParticipation ? ((stats.not_entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;

    const participationData = stats ? {
        labels: [
            `Votaron: ${stats.voted} (${votedPct}%)`,
            `Ingresaron pero NO Votaron: ${stats.entered_not_voted} (${enteredNotVotedPct}%)`,
            `NO Ingresaron y NO Votaron: ${stats.not_entered_not_voted} (${notEnteredNotVotedPct}%)`
        ],
        datasets: [{
            data: [stats.voted, stats.entered_not_voted, stats.not_entered_not_voted],
            backgroundColor: ['#3b82f6', '#f59e0b', '#64748b'],
            borderWidth: 1,
        }]
    } : null;

    const totalRegistrados = stats?.total_users || 0;
    const totalIngresaron = stats ? (stats.voted + stats.entered_not_voted) : 0;
    const funnelIngresaronPct = totalRegistrados ? ((totalIngresaron / totalRegistrados) * 100).toFixed(1) : 0;
    const funnelVotaronPct = totalRegistrados ? ((stats.voted / totalRegistrados) * 100).toFixed(1) : 0;

    const funnelData = stats ? {
        labels: [
            `Total Registrados (${totalRegistrados} - 100%)`,
            `Ingresaron (${totalIngresaron} - ${funnelIngresaronPct}%)`,
            `Votaron (${stats.voted} - ${funnelVotaronPct}%)`
        ],
        datasets: [{
            label: 'Cantidad de Usuarios',
            data: [
                stats.total_users,
                stats.voted + stats.entered_not_voted,
                stats.voted
            ],
            backgroundColor: ['#8b5cf6', '#0ea5e9', '#14b8a6'],
            borderWidth: 1,
        }]
    } : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { font: { size: 14, weight: 'bold' }, color: '#333' }
            }
        }
    };

    const barOptions = {
        ...chartOptions,
        indexAxis: 'y',
        plugins: { ...chartOptions.plugins, title: { display: true, text: 'Embudo de Conversión', font: { size: 16 } } }
    };

    return (
        <div className="w-full font-sans space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800">Módulo de Informes y Reportes</h1>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
                        Pfizer VRS — Panel de Control Administrativo
                    </p>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap border-b border-slate-200 bg-white p-2 rounded-2xl shadow-sm gap-2">
                <button
                    onClick={() => setActiveTab('stats')}
                    className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeTab === 'stats' ? 'bg-[#00595b] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    📊 Gráficos Globales
                </button>

                <button
                    onClick={() => setActiveTab('pre_attendance')}
                    className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeTab === 'pre_attendance' ? 'bg-[#00595b] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    📋 Pre-Asistencia ({preAttendanceUsers.length})
                </button>

                <button
                    onClick={() => setActiveTab('event_attendance')}
                    className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeTab === 'event_attendance' ? 'bg-[#00595b] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    🎟️ Asistencia Evento ({eventAttendanceUsers.length})
                </button>

                <button
                    onClick={() => setActiveTab('all_users_breakdown')}
                    className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeTab === 'all_users_breakdown' ? 'bg-[#00595b] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    👥 Detalle Usuarios (Ingreso/Firma/Voto) ({userBreakdownList.length})
                </button>

                <button
                    onClick={() => setActiveTab('polls_trivia')}
                    className={`px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeTab === 'polls_trivia' ? 'bg-[#00595b] text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                >
                    ❓ Votaciones & Trivias ({pollsList.length})
                </button>
            </div>

            {/* TAB 1: GLOBAL STATS DASHBOARD */}
            {activeTab === 'stats' && (
                <div>
                    <div className="bg-white p-4 rounded-xl shadow-md mb-8 flex flex-wrap gap-4 items-end">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Sala</label>
                            <select
                                value={selectedSala}
                                onChange={(e) => setSelectedSala(e.target.value)}
                                className="w-64 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                            >
                                <option value="">Todas las Salas</option>
                                {salas.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Fecha</label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="w-48 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Exclusión de Usuarios</label>
                            <button
                                onClick={() => setIsExcludeModalOpen(true)}
                                className="p-2 border border-slate-300 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-800 flex items-center gap-2"
                            >
                                <span>🚫</span> Excluir del Conteo ({excludedUserIds.length})
                            </button>
                        </div>

                        <div className="ml-auto">
                            <button
                                onClick={handleExportPDF}
                                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider shadow-md transition-all flex items-center"
                            >
                                <span className="mr-2">📄</span> Exportar Dashboard Completo (Captura PDF)
                            </button>
                        </div>
                    </div>

                    {/* Exclude Users Modal */}
                    {isExcludeModalOpen && (
                        <div className="fixed inset-0 z-[99999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-3xl p-6 w-full max-w-2xl shadow-2xl space-y-4 border border-slate-200">
                                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                    <h3 className="text-lg font-black text-slate-800">🚫 Excluir Usuarios del Conteo de Estadísticas</h3>
                                    <button onClick={() => setIsExcludeModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-black text-xl">✕</button>
                                </div>
                                <p className="text-xs text-slate-500 font-semibold">
                                    Marca las cuentas que deseas excluir de los cálculos globales (ej. staff, cuentas de prueba, moderadores).
                                </p>
                                <div className="flex gap-2 text-xs">
                                    <button
                                        onClick={() => {
                                            const staffIds = allUsers.filter(u => ['staff', 'admin', 'director'].includes(u.role)).map(u => u.id);
                                            setExcludedUserIds(staffIds);
                                        }}
                                        className="px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 font-bold rounded-lg"
                                    >
                                        Excluir Todo el Staff / Admin
                                    </button>
                                    <button
                                        onClick={() => setExcludedUserIds([])}
                                        className="px-3 py-1.5 bg-slate-200 text-slate-700 hover:bg-slate-300 font-bold rounded-lg"
                                    >
                                        Limpiar Exclusiones (Incluir Todos)
                                    </button>
                                </div>
                                <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                                    {allUsers.map(u => (
                                        <label key={u.id} className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={excludedUserIds.includes(u.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setExcludedUserIds(prev => [...prev, u.id]);
                                                        else setExcludedUserIds(prev => prev.filter(x => x !== u.id));
                                                    }}
                                                    className="w-4 h-4 accent-red-600 rounded cursor-pointer"
                                                />
                                                <div>
                                                    <p className="text-xs font-bold text-slate-800">{u.name} {u.last_name} ({u.identification || 'Sin ID'})</p>
                                                    <p className="text-[10px] text-slate-400">{u.email} • Rol: <span className="font-bold uppercase text-purple-600">{u.role}</span></p>
                                                </div>
                                            </div>
                                            {excludedUserIds.includes(u.id) && (
                                                <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full">Excluido</span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                                <div className="flex justify-end pt-2">
                                    <button
                                        onClick={() => setIsExcludeModalOpen(false)}
                                        className="bg-[#00595b] text-white px-6 py-2.5 rounded-xl font-bold text-xs uppercase"
                                    >
                                        Aplicar Exclusiones
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!stats ? (
                        <div className="p-12 text-center text-slate-500 font-bold">Cargando estadísticas...</div>
                    ) : (
                        <div ref={reportRef} className="bg-gray-100 rounded-xl relative space-y-4">
                            <div className="pdf-page w-full bg-white p-8 rounded-2xl shadow-sm" style={{ minHeight: '270mm' }}>
                                <div className="text-center mb-10">
                                    <h2 className="text-3xl font-black text-slate-800 uppercase tracking-wide">Informe Estadístico Global</h2>
                                    <p className="text-sm text-slate-500 mt-1">
                                        {selectedSala ? `Sala ID: ${selectedSala}` : 'Todas las Salas'} |
                                        {selectedDate ? ` Fecha: ${selectedDate}` : ' Histórico Completo'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                                        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Total Usuarios</h3>
                                        <p className="text-4xl font-black text-emerald-600 mt-2">{stats.total_users}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                                        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Staff Registrado</h3>
                                        <p className="text-4xl font-black text-purple-600 mt-2">{stats.staff_users}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                                        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Con Firma</h3>
                                        <p className="text-4xl font-black text-green-500 mt-2">{stats.with_signature}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm text-center">
                                        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Total Participantes</h3>
                                        <p className="text-4xl font-black text-amber-500 mt-2">{stats.voted + stats.entered_not_voted}</p>
                                    </div>
                                </div>

                                <div className="mb-10">
                                    <h3 className="text-xl font-bold text-slate-800 text-center mb-6">Estado de Firmas</h3>
                                    <div className="h-[350px] w-full flex justify-center">
                                        <div className="w-full max-w-md">
                                            {signatureData && <Pie data={signatureData} options={chartOptions} />}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pdf-page w-full bg-white p-8 rounded-2xl shadow-sm" style={{ minHeight: '270mm' }}>
                                <div className="mb-12">
                                    <h3 className="text-xl font-bold text-slate-800 text-center mb-6">Nivel de Participación Global</h3>
                                    <div className="h-[350px] w-full flex justify-center">
                                        <div className="w-full max-w-md">
                                            {participationData && <Doughnut data={participationData} options={chartOptions} />}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-10">
                                    <h3 className="text-xl font-bold text-slate-800 text-center mb-6">Embudo de Conversión (Ingresos y Votos)</h3>
                                    <div className="h-[350px] w-full">
                                        {funnelData && <Bar data={funnelData} options={barOptions} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: PRE-ATTENDANCE USER REPORT */}
            {activeTab === 'pre_attendance' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <h2 className="text-xl font-black text-slate-800">Listado de Usuarios — Pre-Asistencia Confirmada</h2>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                Médicos que pre-confirmaron asistencia y seleccionaron ciudad
                            </p>
                        </div>

                        <button
                            onClick={() => {
                                const selectedObjs = preAttendanceUsers.filter(u => preSelected.includes(u.id));
                                exportSelectedToVectorPDF("Pre_Asistencia_Confirmada", selectedObjs, "pre_asistencia");
                            }}
                            className="bg-[#00595b] hover:bg-[#00484c] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center"
                        >
                            <span className="mr-2 text-base">📄</span> Exportar Seleccionados a PDF (Texto Vectorial)
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                type="text"
                                placeholder="Buscar por CMP, Nombre o Email..."
                                value={preSearch}
                                onChange={(e) => setPreSearch(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold w-64 focus:outline-none focus:ring-2 focus:ring-[#00595b]"
                            />

                            <select
                                value={preCityFilter}
                                onChange={(e) => setPreCityFilter(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold bg-white cursor-pointer focus:outline-none"
                            >
                                <option value="">Todas las Ciudades</option>
                                <option value="Arequipa">Arequipa</option>
                                <option value="Piura">Piura</option>
                                <option value="Lima">Lima</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-600 mr-2">
                                {preSelected.length} de {preAttendanceUsers.length} Seleccionados
                            </span>
                            <button onClick={selectAllPre} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Seleccionar Todos
                            </button>
                            <button onClick={deselectAllPre} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Desmarcar Todos
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-inner">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-100 text-slate-700 text-[11px] font-black uppercase tracking-wider border-b border-slate-200">
                                    <th className="p-3 text-center w-12">
                                        <input
                                            type="checkbox"
                                            checked={preAttendanceUsers.length > 0 && preSelected.length === preAttendanceUsers.length}
                                            onChange={(e) => e.target.checked ? selectAllPre() : deselectAllPre()}
                                            className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                        />
                                    </th>
                                    <th className="p-3">CMP / ID</th>
                                    <th className="p-3">Nombre Completo</th>
                                    <th className="p-3">Correo Electrónico</th>
                                    <th className="p-3">Ciudad</th>
                                    <th className="p-3">Fecha Pre-Asistencia</th>
                                    <th className="p-3 text-center">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-700">
                                {preAttendanceUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-slate-400 font-bold">
                                            No se encontraron registros de pre-asistencia.
                                        </td>
                                    </tr>
                                ) : (
                                    preAttendanceUsers.map((u) => {
                                        const isSelected = preSelected.includes(u.id);
                                        return (
                                            <tr
                                                key={u.id}
                                                onClick={() => togglePreUser(u.id)}
                                                className={`cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-emerald-50/70 hover:bg-emerald-100/70' : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => togglePreUser(u.id)}
                                                        className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                                    />
                                                </td>
                                                <td className="p-3 font-bold text-slate-900">{u.identification || '—'}</td>
                                                <td className="p-3 font-bold text-slate-800">{u.name} {u.last_name}</td>
                                                <td className="p-3 text-slate-600">{u.email}</td>
                                                <td className="p-3">
                                                    <span className="px-2.5 py-1 bg-slate-100 text-slate-800 font-bold rounded-lg text-[10px] uppercase">
                                                        {u.city || '—'}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-slate-500">
                                                    {u.asistire_date ? new Date(u.asistire_date).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : (u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—')}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 font-black rounded-full text-[10px] uppercase">
                                                        Pre-Confirmado
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 3: EVENT ATTENDANCE USER REPORT */}
            {activeTab === 'event_attendance' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <h2 className="text-xl font-black text-slate-800">Listado de Usuarios — Asistencia Presencial en el Evento</h2>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                Médicos que confirmaron su ingreso presencial mediante QR / Escáner
                            </p>
                        </div>

                        <button
                            onClick={() => {
                                const selectedObjs = eventAttendanceUsers.filter(u => eventSelected.includes(u.id));
                                exportSelectedToVectorPDF("Asistencia_Presencial_Evento", selectedObjs, "asistencia_presencial");
                            }}
                            className="bg-[#00595b] hover:bg-[#00484c] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center"
                        >
                            <span className="mr-2 text-base">📄</span> Exportar Seleccionados a PDF (Texto Vectorial)
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                type="text"
                                placeholder="Buscar por CMP, Nombre o Email..."
                                value={eventSearch}
                                onChange={(e) => setEventSearch(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold w-64 focus:outline-none focus:ring-2 focus:ring-[#00595b]"
                            />

                            <select
                                value={eventCityFilter}
                                onChange={(e) => setEventCityFilter(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold bg-white cursor-pointer focus:outline-none"
                            >
                                <option value="">Todas las Ciudades</option>
                                <option value="Lima">Lima</option>
                                <option value="Arequipa">Arequipa</option>
                                <option value="Piura">Piura</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-600 mr-2">
                                {eventSelected.length} de {eventAttendanceUsers.length} Seleccionados
                            </span>
                            <button onClick={selectAllEvent} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Seleccionar Todos
                            </button>
                            <button onClick={deselectAllEvent} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Desmarcar Todos
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-inner">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-100 text-slate-700 text-[11px] font-black uppercase tracking-wider border-b border-slate-200">
                                    <th className="p-3 text-center w-12">
                                        <input
                                            type="checkbox"
                                            checked={eventAttendanceUsers.length > 0 && eventSelected.length === eventAttendanceUsers.length}
                                            onChange={(e) => e.target.checked ? selectAllEvent() : deselectAllEvent()}
                                            className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                        />
                                    </th>
                                    <th className="p-3">CMP / ID</th>
                                    <th className="p-3">Nombre Completo</th>
                                    <th className="p-3">Correo Electrónico</th>
                                    <th className="p-3">Ciudad</th>
                                    <th className="p-3">Fecha / Hora Ingreso</th>
                                    <th className="p-3 text-center">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-700">
                                {eventAttendanceUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-slate-400 font-bold">
                                            No se encontraron registros de asistencia presencial al evento.
                                        </td>
                                    </tr>
                                ) : (
                                    eventAttendanceUsers.map((u) => {
                                        const isSelected = eventSelected.includes(u.id);
                                        return (
                                            <tr
                                                key={u.id}
                                                onClick={() => toggleEventUser(u.id)}
                                                className={`cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-blue-50/70 hover:bg-blue-100/70' : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleEventUser(u.id)}
                                                        className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                                    />
                                                </td>
                                                <td className="p-3 font-bold text-slate-900">{u.identification || '—'}</td>
                                                <td className="p-3 font-bold text-slate-800">{u.name} {u.last_name}</td>
                                                <td className="p-3 text-slate-600">{u.email}</td>
                                                <td className="p-3">
                                                    <span className="px-2.5 py-1 bg-slate-100 text-slate-800 font-bold rounded-lg text-[10px] uppercase">
                                                        {u.city || 'Lima'}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-slate-500">
                                                    {u.asistencia_evento_date ? new Date(u.asistencia_evento_date).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : (u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—')}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <span className="px-2.5 py-1 bg-blue-100 text-blue-700 font-black rounded-full text-[10px] uppercase">
                                                        Ingreso Confirmado
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 4: COMPLETE USER BREAKDOWN (INGRESO SÍ/NO, FIRMA SÍ/NO, VOTO SÍ/NO) */}
            {activeTab === 'all_users_breakdown' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <h2 className="text-xl font-black text-slate-800">Detalle General de Usuarios</h2>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                Listado completo con selección de columnas (Ciudad, Sala, Confirmación, Firma y Voto)
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={exportBreakdownToExcel}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center"
                            >
                                <span className="mr-2 text-base">📊</span> Exportar a Excel
                            </button>

                            <button
                                onClick={() => {
                                    const selectedObjs = userBreakdownList.filter(u => breakdownSelected.includes(u.id || `${u.name}-${u.last_name}`));
                                    exportSelectedToVectorPDF("Detalle_General_Usuarios", selectedObjs, "breakdown");
                                }}
                                className="bg-[#00595b] hover:bg-[#00484c] text-white px-5 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center"
                            >
                                <span className="mr-2 text-base">📄</span> Exportar a PDF
                            </button>
                        </div>
                    </div>

                    {/* Column Selection Panel */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                        <div className="flex justify-between items-center">
                            <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-2">
                                <span>⚙️</span> Columnas a Visualizar / Exportar ({selectedBreakdownColumns.length} de {ALL_BREAKDOWN_COLUMNS.length})
                            </h3>
                            <button
                                onClick={selectAllBreakdownColumns}
                                className="text-[11px] font-bold text-[#00595b] hover:underline cursor-pointer"
                            >
                                Seleccionar Todas
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2.5">
                            {ALL_BREAKDOWN_COLUMNS.map(col => {
                                const isChecked = selectedBreakdownColumns.includes(col.key);
                                return (
                                    <label
                                        key={col.key}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold cursor-pointer transition-all ${
                                            isChecked 
                                                ? 'bg-[#00595b]/10 border-[#00595b] text-[#00595b]' 
                                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleBreakdownColumn(col.key)}
                                            className="w-3.5 h-3.5 accent-[#00595b] rounded cursor-pointer"
                                        />
                                        <span>{col.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                type="text"
                                placeholder="Buscar por CMP, Nombre, Email o Ciudad..."
                                value={breakdownSearch}
                                onChange={(e) => setBreakdownSearch(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold w-64 focus:outline-none focus:ring-2 focus:ring-[#00595b]"
                            />

                            <select
                                value={breakdownSalaFilter}
                                onChange={(e) => setBreakdownSalaFilter(e.target.value)}
                                className="p-2.5 border border-slate-300 rounded-xl text-xs font-bold bg-white cursor-pointer focus:outline-none"
                            >
                                <option value="">Todas las Salas</option>
                                {salas.map(s => (
                                    <option key={s.id} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-600 mr-2">
                                {breakdownSelected.length} de {userBreakdownList.length} Seleccionados
                            </span>
                            <button onClick={selectAllBreakdown} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Seleccionar Todos
                            </button>
                            <button onClick={deselectAllBreakdown} className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition-all">
                                Desmarcar Todos
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-inner">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-100 text-slate-700 text-[11px] font-black uppercase tracking-wider border-b border-slate-200">
                                    <th className="p-3 text-center w-12">
                                        <input
                                            type="checkbox"
                                            checked={userBreakdownList.length > 0 && breakdownSelected.length === userBreakdownList.length}
                                            onChange={(e) => e.target.checked ? selectAllBreakdown() : deselectAllBreakdown()}
                                            className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                        />
                                    </th>
                                    {selectedBreakdownColumns.includes('identification') && <th className="p-3">CMP / ID</th>}
                                    {selectedBreakdownColumns.includes('name') && <th className="p-3">Nombre Completo</th>}
                                    {selectedBreakdownColumns.includes('email') && <th className="p-3">Correo Electrónico</th>}
                                    {selectedBreakdownColumns.includes('city') && <th className="p-3">Ciudad</th>}
                                    {selectedBreakdownColumns.includes('sala_name') && <th className="p-3">Sala</th>}
                                    {selectedBreakdownColumns.includes('ingreso') && <th className="p-3 text-center">Ingreso / Confirmación</th>}
                                    {selectedBreakdownColumns.includes('firmo') && <th className="p-3 text-center">Firma Registrada</th>}
                                    {selectedBreakdownColumns.includes('voto') && <th className="p-3 text-center">Voto Registrado</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-xs font-semibold text-slate-700">
                                {userBreakdownList.length === 0 ? (
                                    <tr>
                                        <td colSpan={1 + selectedBreakdownColumns.length} className="p-8 text-center text-slate-400 font-bold">
                                            No se encontraron usuarios para mostrar.
                                        </td>
                                    </tr>
                                ) : (
                                    userBreakdownList.map((u, idx) => {
                                        const keyId = u.id || `${u.name}-${u.last_name}-${idx}`;
                                        const isSelected = breakdownSelected.includes(keyId);
                                        return (
                                            <tr
                                                key={keyId}
                                                onClick={() => toggleBreakdownUser(keyId)}
                                                className={`cursor-pointer transition-colors ${
                                                    isSelected ? 'bg-slate-100/90 hover:bg-slate-200/80' : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleBreakdownUser(keyId)}
                                                        className="w-4 h-4 rounded accent-[#00595b] cursor-pointer"
                                                    />
                                                </td>
                                                {selectedBreakdownColumns.includes('identification') && (
                                                    <td className="p-3 font-bold text-slate-900">{u.identification || '—'}</td>
                                                )}
                                                {selectedBreakdownColumns.includes('name') && (
                                                    <td className="p-3 font-bold text-slate-800">{u.name} {u.last_name}</td>
                                                )}
                                                {selectedBreakdownColumns.includes('email') && (
                                                    <td className="p-3 text-slate-600">{u.email || '—'}</td>
                                                )}
                                                {selectedBreakdownColumns.includes('city') && (
                                                    <td className="p-3">
                                                        <span className="px-2.5 py-1 bg-slate-100 text-slate-800 font-bold rounded-lg text-[10px] uppercase">
                                                            {u.city || '—'}
                                                        </span>
                                                    </td>
                                                )}
                                                {selectedBreakdownColumns.includes('sala_name') && (
                                                    <td className="p-3 text-slate-600">{u.sala_name || 'Sin Sala'}</td>
                                                )}
                                                {selectedBreakdownColumns.includes('ingreso') && (
                                                    <td className="p-3 text-center font-bold">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-black ${
                                                            u.ingreso === 'Sí' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'
                                                        }`}>
                                                            {u.ingreso}
                                                        </span>
                                                    </td>
                                                )}
                                                {selectedBreakdownColumns.includes('firmo') && (
                                                    <td className="p-3 text-center font-bold">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-black ${
                                                            u.firmo === 'Sí' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'
                                                        }`}>
                                                            {u.firmo}
                                                        </span>
                                                    </td>
                                                )}
                                                {selectedBreakdownColumns.includes('voto') && (
                                                    <td className="p-3 text-center font-bold">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-black ${
                                                            u.voto === 'Sí' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'
                                                        }`}>
                                                            {u.voto}
                                                        </span>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 5: INTERACTIVE POLLS & TRIVIAS STATS REPORT */}
            {activeTab === 'polls_trivia' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <h2 className="text-xl font-black text-slate-800">Resultados de Votaciones y Preguntas Interactivas</h2>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                Respuestas, opciones correctas, distribución de votos y participación por pregunta
                            </p>
                        </div>

                        <button
                            onClick={exportPollsToVectorPDF}
                            className="bg-[#00595b] hover:bg-[#00484c] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-all flex items-center"
                        >
                            <span className="mr-2 text-base">📄</span> Exportar Resultados a PDF (Texto Vectorial)
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                        {pollsList.length === 0 ? (
                            <div className="p-12 text-center text-slate-400 font-bold border border-dashed border-slate-200 rounded-2xl">
                                No hay preguntas o trivias registradas en el sistema.
                            </div>
                        ) : (
                            pollsList.map((poll, idx) => {
                                const totalVotes = poll.stats?.totalVotes || 0;

                                return (
                                    <div key={poll.id || idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="px-3 py-1 bg-blue-100 text-blue-700 font-black text-[10px] uppercase rounded-full tracking-wider">
                                                    Pregunta {idx + 1}
                                                </span>
                                                <h3 className="text-lg font-bold text-slate-900 mt-2">
                                                    {poll.question}
                                                </h3>
                                            </div>
                                            <div className="text-right">
                                                <span className="block text-2xl font-black text-emerald-600">{totalVotes}</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Votos</span>
                                            </div>
                                        </div>

                                        <div className="space-y-3 pt-2">
                                            {(poll.stats?.results || poll.options || []).map((res, rIdx) => {
                                                const votes = res.votes || 0;
                                                const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;

                                                return (
                                                    <div key={rIdx} className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-1.5">
                                                        <div className="flex justify-between items-center text-xs">
                                                            <span className="font-bold text-slate-800 flex items-center gap-2">
                                                                {res.is_correct && <span className="text-emerald-500 font-black">✓ Correcta:</span>}
                                                                {res.option_text || res.text}
                                                            </span>
                                                            <span className="font-black text-slate-700">
                                                                {votes} votos ({percent}%)
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full transition-all ${
                                                                    res.is_correct ? 'bg-emerald-500' : 'bg-blue-500'
                                                                }`}
                                                                style={{ width: `${percent}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;
